import { computed, toValue, watchEffect, type MaybeRefOrGetter } from 'vue';
import { useHead } from '@vueuse/head';
import type { RouteLocationNormalized } from 'vue-router';

const APP_NAME = 'Logora';
const TITLE_SEPARATOR = ' - ';

const normalizeTitle = (title?: string | null): string | null => {
  const value = title?.toString().trim();
  return value && value.length > 0 ? value : null;
};

/** Builds a branded title like "Page - Logora". */
export const buildPageTitle = (title?: string | null): string => {
  const normalized = normalizeTitle(title);
  return normalized ? `${normalized}${TITLE_SEPARATOR}${APP_NAME}` : APP_NAME;
};

/** Syncs `document.title` in browser environments. */
export const syncDocumentTitle = (title?: string | null): string => {
  const nextTitle = buildPageTitle(title);
  if (typeof document !== 'undefined') {
    document.title = nextTitle;
  }
  return nextTitle;
};

type RouteTitle = string | ((route: RouteLocationNormalized) => string | null | undefined);

/** Reads the title out of route meta, falling back to the route name. */
export const resolveRouteTitle = (route: RouteLocationNormalized): string | null => {
  const metaTitle = route.meta?.pageTitle as RouteTitle | undefined;
  if (typeof metaTitle === 'function') {
    return metaTitle(route) ?? null;
  }
  if (typeof metaTitle === 'string') {
    return metaTitle;
  }
  return route.name ? String(route.name) : null;
};

/** Applies the title for a given route to the document. */
export const applyRouteTitle = (route: RouteLocationNormalized): string => {
  return syncDocumentTitle(resolveRouteTitle(route));
};

/** Reactive page title helper that keeps head + document in sync. */
export const usePageTitle = (title: MaybeRefOrGetter<string | null | undefined>) => {
  const computedTitle = computed(() => buildPageTitle(toValue(title)));

  useHead(() => ({
    title: computedTitle.value,
  }));

  if (typeof document !== 'undefined') {
    watchEffect(() => {
      document.title = computedTitle.value;
    });
  }

  return computedTitle;
};
